import { Checkbox, TableSortLabel } from '@material-ui/core'
import React, { Fragment, useCallback, useEffect, useMemo, useState, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Direction } from 'src/graphql-types/globalTournamentTypes'
import { SpacingProps } from 'src/hooks/spacing'
import {
  retrieveRowsPerPage,
  storeRowsPerPage,
  storeColumnPreference
} from 'src/utils/storage/local-storage'
import APIErrorMessage from '../api-error-message/api-error-message'
import Spinner from '../spinner/spinner'
import { ControlProps, SortVariables } from '../table-controls/table-controls'
import {
  Table,
  TBody,
  TD,
  TH,
  THead,
  TPagination,
  TPaginationInfoTop,
  TR,
  TRProps
} from '../table/table'
import * as styles from './admin-table.module.less'
import { NoWrap } from '@clubspark-react/clubspark-react-tools'
import { CustomGrid } from '../custom-grid/custom-grid'
import { TableColumnButton } from '../table-column-button/table-column-button'

export type ColumnToggle = {
  checked: boolean
  disabled?: boolean
  index?: number
}

interface Props<T extends Datum> extends SpacingProps {
  columns: DataCols<T>
  data?: T[] | null
  hasCheckbox?: boolean
  rowIsDisabled?: (t: T) => boolean
  loading?: boolean
  error?: string
  controls?: ControlProps
  selectedRows?: string[]
  setSelectedRows?: (rows: string[]) => void
  onRowClick?: (t: T, event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void
  fixedWidth?: boolean
  hideControlsOnLoading?: boolean
  noRowPaddng?: boolean
  noDataComponent?: ReactNode
  disableRowsPerPage?: boolean
  rowsPerPageOptions?: number[]
  rowsPerPageExpanded?: boolean
  hideTopPaginationInfo?: ReactNode
  columnSelectorId?: string
  customPlayerPagination?: string | number // This is a temporary fix, will likely get removed soon
  filters?: any
  rowProps?: TRProps
  defaultSortColumn?: SortVariables
}

export interface Datum {
  id: string
  key?: string
}

export type DataCols<T extends Datum> = {
  key: string
  title?: React.ReactNode
  getValue: (t: T) => React.ReactNode
  hidden?: boolean
  widthClassName?: string
  noWrapTitle?: boolean
  noPadding?: boolean
  sort?: boolean
  hideHeaderDivider?: boolean
  hideCellDivider?: boolean
  columnToggle?: ColumnToggle
}[]

export function useColumnSelector(columns, columnSelectorId?: string) {
  const columnToggle = useMemo(() => columns.filter(col => !col.hidden), [columns])
  const [colsToggle, setColsToggle] = useState(() => columnToggle)

  useEffect(() => {
    setColsToggle(() => columnToggle)
  }, [columnToggle])

  useEffect(() => {
    if (columnSelectorId && colsToggle) {
      const data = colsToggle.reduce((prev, curr) => {
        const col =
          typeof curr?.columnToggle?.checked === 'boolean'
            ? { [curr.accessor || curr.key]: curr.columnToggle.checked }
            : {}
        return { ...prev, ...col }
      }, {})
      storeColumnPreference(columnSelectorId, data)
    }
  }, [columnSelectorId, colsToggle])

  return { colsToggle, setColsToggle }
}

function AdminTable<T extends Datum>(props: Props<T>) {
  const {
    columns,
    hasCheckbox,
    selectedRows,
    onRowClick,
    setSelectedRows,
    controls,
    loading,
    error,
    hideControlsOnLoading,
    rowIsDisabled,
    noRowPaddng,
    noDataComponent,
    disableRowsPerPage,
    rowsPerPageOptions,
    rowsPerPageExpanded,
    hideTopPaginationInfo,
    columnSelectorId,
    customPlayerPagination,
    filters,
    defaultSortColumn,
    rowProps,
    ...tableProps
  } = props
  const data = props.data ?? []
  const { t } = useTranslation()
  const headers = useMemo(() => columns.filter(c => c.title && !c.hidden), [columns])
  const [page, setPage] = useState(0)
  const storedRowsPerPage = useMemo(() => retrieveRowsPerPage(), [])
  const defaultRows = rowsPerPageOptions ? rowsPerPageOptions[0] : 10
  const [rowsPerPage, setRowsPerPage] = useState(storedRowsPerPage ?? defaultRows)
  const [sort, setSort] = useState<SortVariables>()

  const { colsToggle, setColsToggle } = useColumnSelector(columns, columnSelectorId)

  useEffect(() => {
    if (defaultSortColumn) setSort(defaultSortColumn)
  }, [])

  const isColumnHiddenManually = (key: string) => {
    const col = colsToggle.find(col => col.key === key)
    return col?.columnToggle?.checked === false
  }

  const handleChangePage = useCallback(
    (_, newPage: number) => {
      setPage(newPage)
    },
    [setPage]
  )
  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(+event.target.value)
      setPage(0)
    },
    [setPage, setRowsPerPage]
  )

  useEffect(() => {
    // This prevents tables that don't have rowsPerPageExpanded from breaking
    if (rowsPerPage !== 100) storeRowsPerPage(rowsPerPage)
  }, [rowsPerPage])

  const { onControlChange } = controls ?? {}

  useEffect(() => {
    onControlChange?.({
      pagination: {
        offset: page * rowsPerPage,
        limit: rowsPerPage
      },
      sorting: sort
    })
  }, [page, rowsPerPage, onControlChange, sort])

  useEffect(() => setPage(0), [filters])

  const handleSelectAllClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedRows?.(event.target.checked ? data.map(d => d.id) : [])
    },
    [data, setSelectedRows]
  )

  const onSort = useCallback(
    property => {
      const isAsc = sort?.orderBy === property && sort?.order === Direction.ASCENDING
      setSort({ order: isAsc ? Direction.DESCENDING : Direction.ASCENDING, orderBy: property })
    },
    [sort]
  )

  useEffect(() => {
    controls?.dataSetKey !== undefined && setPage(0)
  }, [controls?.dataSetKey])

  if (!loading && !error && data.length === 0 && noDataComponent) {
    return <>{noDataComponent}</>
  }

  const isPaginationInfoVisible =
    controls &&
    !controls?.paginaitonDisabled &&
    !(hideControlsOnLoading && loading) &&
    !hideTopPaginationInfo

  const paginationInfo = {
    from: (controls?.totalItems ? 1 : 0) + rowsPerPage * page,
    to: rowsPerPage * page + data.length,
    total: customPlayerPagination ?? controls?.totalItems ?? 0
  }

  return (
    <>
      <CustomGrid
        container
        justify="space-between"
        alignItems="center"
        spacing={{ margins: { sm: 'bottom' } }}
        className={styles.topTableInfoContainer}
        hide={!isPaginationInfoVisible && !columnSelectorId}
      >
        {isPaginationInfoVisible && (
          <div>
            <TPaginationInfoTop
              from={paginationInfo.from}
              to={paginationInfo.to}
              total={paginationInfo.total}
            />
          </div>
        )}
        {hideTopPaginationInfo && hideTopPaginationInfo}
        {columnSelectorId && (
          <TableColumnButton
            cols={colsToggle}
            setCols={setColsToggle}
            spacing={hideTopPaginationInfo ? { margins: { sm: 'bottom' } } : {}}
          />
        )}
      </CustomGrid>

      <Table {...tableProps}>
        <THead>
          <TR>
            {hasCheckbox && (
              <TH>
                <Checkbox
                  classes={{
                    root: styles.checkboxRoot,
                    colorSecondary: styles.checkboxColorSecondary
                  }}
                  indeterminate={!!(selectedRows?.length && selectedRows.length === data.length)}
                  checked={selectedRows && selectedRows.length === data.length}
                  onChange={handleSelectAllClick}
                />
              </TH>
            )}
            {headers.map(t => {
              if (!isColumnHiddenManually(t.key)) {
                return (
                  <TH key={t.key} widthClassName={t.widthClassName} noDivider={t.hideHeaderDivider}>
                    {controls && !controls.sortDisabled && t.sort !== false ? (
                      <TableSortLabel
                        active={sort?.orderBy === t.key}
                        direction={
                          sort?.orderBy === t.key && sort?.order === Direction.DESCENDING
                            ? 'desc'
                            : 'asc'
                        }
                        onClick={() => onSort(t.key)}
                      >
                        <Title {...t} />
                      </TableSortLabel>
                    ) : (
                      <Title {...t} />
                    )}
                  </TH>
                )
              }
            })}
          </TR>
        </THead>
        {!loading && (
          <TBody>
            {data.map(t => (
              <TR
                key={t.key ? t.key : t.id}
                onClick={onRowClick && (e => onRowClick(t, e))}
                disabled={rowIsDisabled?.(t)}
                {...rowProps}
              >
                {columns.map(
                  ({ key, getValue, hidden, widthClassName, noPadding, hideCellDivider }) => (
                    <Fragment key={key}>
                      {!hidden && !isColumnHiddenManually(key) && (
                        <TD
                          widthClassName={widthClassName}
                          noDivider={hideCellDivider}
                          noPad={noPadding || noRowPaddng}
                        >
                          {getValue(t)}
                        </TD>
                      )}
                    </Fragment>
                  )
                )}
              </TR>
            ))}
          </TBody>
        )}
      </Table>
      {(loading || error || data.length === 0) && (
        <div className={styles.loading}>
          {!loading && !error && data.length === 0 && t('no results')}
          {loading && <Spinner />}
          <APIErrorMessage error={error} />
        </div>
      )}
      {controls && !controls?.paginaitonDisabled && !(hideControlsOnLoading && loading) && (
        <TPagination
          count={controls?.totalItems ?? 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          disableRowsPerPage={disableRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          rowsPerPageExpanded={rowsPerPageExpanded}
          customPlayerPagination={customPlayerPagination}
        />
      )}
    </>
  )
}

interface TitleProps {
  noWrapTitle?: boolean
  title?: React.ReactNode
}

const Title: React.FC<TitleProps> = ({ noWrapTitle, title }) => {
  return <>{noWrapTitle ? <NoWrap>{title}</NoWrap> : title}</>
}

export default AdminTable
